import { useEffect, useCallback, useContext } from 'react';
import { MessageType, SessionType } from 'models/Message';
import bus, { CallbackBusType, StandardEventTypes } from 'modules/bus';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import MessagingContext from '../../../providers/Messaging/Messaging.context';

type ChatListeners = {
  setMessages?: React.Dispatch<React.SetStateAction<MessageType[]>>;
  onMessageArrived: (message: MessageType) => void;
  onMessageSent?: (data: CallbackBusType<MessageType>) => void;
  onBulkSessions?: (data: CallbackBusType<Array<SessionType>>) => void;
};

export default function useChatListener(args: ChatListeners) {
  const { setMessages, onMessageSent, onMessageArrived, onBulkSessions } = args;

  const { sessionId } = useContext(MessagingContext);

  const onNewMessage = useCallback(
    (message: MessageType) => {
      if (!sessionId) return;
      if (setMessages && sessionId === message.sessionId)
        setMessages((oldMessages) => [message, ...oldMessages]);

      onMessageArrived?.(message);
    },
    [sessionId, onMessageArrived, setMessages],
  );

  useSocketEvent(LISTEN_EVENTS.MESSAGE_CREATED, onNewMessage);

  useEffect(() => {
    // setup listeners for chat session
    if (onMessageSent)
      bus.addEventListener(StandardEventTypes.MESSAGE_SENT, onMessageSent);

    if (onBulkSessions)
      bus.addEventListener(StandardEventTypes.BULK_SESSION, onBulkSessions);

    return () => {
      if (onMessageSent)
        bus.removeEventListener(StandardEventTypes.MESSAGE_SENT, onMessageSent);

      if (onBulkSessions)
        bus.removeEventListener(
          StandardEventTypes.BULK_SESSION,
          onBulkSessions,
        );
    };
  }, [onMessageSent, onNewMessage, onBulkSessions]);
}
